import React, { useEffect, useCallback } from 'react';
import Checkbox from '@material-ui/core/Checkbox';
import TextField from '@material-ui/core/TextField';
import Autocomplete from '@material-ui/lab/Autocomplete';
import CheckBoxOutlineBlankIcon from '@material-ui/icons/CheckBoxOutlineBlank';
import CheckBoxIcon from '@material-ui/icons/CheckBox';
import { connect } from 'react-redux';
import { INode } from '../../helpers/interfaces';
import { showFilteredNodesNames, showFilteredNodes } from '../../stores/actions/dashboardActions';
import { getNodes } from '../../stores/actions/actions';

const icon = <CheckBoxOutlineBlankIcon fontSize='small' />;
const checkedIcon = <CheckBoxIcon fontSize='small' />;

interface NodeSearchBarProps {
	nodes: INode[];
	dashboardFilteredNodesNames: string[];
	dashboardFilteredNodes: number[];
	systemId: number;
	deleteNodeSuccessfully: boolean;
	showFilteredNodesNames: (filteredNodes: string[]) => void;
	showFilteredNodes: (filteredNodes: number[]) => void;
	getNodes: () => void;
}

const NodeSearchBar = ({
	nodes,
	dashboardFilteredNodesNames,
	dashboardFilteredNodes,
	systemId,
	deleteNodeSuccessfully,
	showFilteredNodesNames,
	showFilteredNodes,
	getNodes,
}: NodeSearchBarProps) => {
	const nodeOptions: string[] = nodes.map((node: INode) => node.name);


	const filterNodesBySystemId = useCallback((filteredNodes: number[]): string[] => {
		const result: string[] = [];
		filteredNodes.forEach((filteredNode) => {
			const node = nodes.find(({ id }) => id === filteredNode);
			if (node && node.systemId === systemId) {
				result.push(node.name);
			}
		});
		return result;
	}, [systemId, nodes]);

	const handleSelectChange = (e, pickedNodesNames) => {
		let pickedNodes: number[] = [];
		const previousPickedNodes = [...dashboardFilteredNodes];
		const nodesFromOtherSystems = [];
		// pick all nodes from the other systems than current system:
		if (previousPickedNodes.length) {
			previousPickedNodes.forEach((nodeId) => {
				const currentNodesId = nodes.map((node) => node.id);
				const res = currentNodesId.find((id) => id === nodeId);
				if (!res) {
					nodesFromOtherSystems.push(nodeId);
				}
			});
		}
		// picked nodes ids from current system:
		pickedNodesNames.forEach((pickedNodeName) => {
			const currentNode = nodes.find((node) => node.name === pickedNodeName);
			if (currentNode) {
				pickedNodes.push(currentNode.id);
			}
		});
		// delete all current system nodes from the store when user clear input:
		if (!pickedNodesNames.length) {
			const nodesToRemove = nodes.map((node) => node.id);
			pickedNodes = dashboardFilteredNodes;
			nodesToRemove.forEach((nodeId) => {
				const index = pickedNodes.findIndex((pickedNodeId) => pickedNodeId === nodeId);
				if (index !== -1) {
					pickedNodes.splice(index, 1);
				}
			});
		}
		// join nodes from the other systems to picked nodes from current system:
		const resultArr = [...nodesFromOtherSystems, ...pickedNodes];
		showFilteredNodes(resultArr);
		// show only these nodes names which system's ID === systemId:
		const filteredPickedNodesNames = filterNodesBySystemId(pickedNodes);
		showFilteredNodesNames(filteredPickedNodesNames);
	};

	useEffect(() => {
		const filteredPickedNodesNames = filterNodesBySystemId(dashboardFilteredNodes);
		showFilteredNodesNames(filteredPickedNodesNames);
	}, [
		dashboardFilteredNodes,
		filterNodesBySystemId,
		showFilteredNodesNames
	]);

	useEffect(() => {
		if (deleteNodeSuccessfully) {
			getNodes([systemId]);
		}
	}, [getNodes, deleteNodeSuccessfully, systemId]);

	return (
		<Autocomplete
			multiple
			id='checkboxes-nodes'
			options={nodeOptions}
			value={dashboardFilteredNodesNames}
			disableCloseOnSelect
			getOptionLabel={(node) => node}
			renderOption={(node, { selected }) => (
				<>
					<Checkbox icon={icon} checkedIcon={checkedIcon} style={{ marginRight: 8, color: 'gray' }} checked={selected} />
					{node}
				</>
			)}
			style={{ backgroundColor: 'rgba(250,250,250,0.75)', margin: '10px 15px 20px 15px' }}
			renderInput={(nodes) => <TextField {...nodes} variant='outlined' label='Search devices' />}
			onChange={(e, pickedNodes) => handleSelectChange(e, pickedNodes)}
			fullWidth
		/>
	);
};

const mapStateToProps = (state) => {
	return {
		nodes: state.dashboard.nodes,
		dashboardFilteredNodesNames: state.dashboard.dashboardFilteredNodesNames,
		dashboardFilteredNodes: state.dashboard.dashboardFilteredNodes,
		deleteNodeSuccessfully: state.installer.deleteNodeSuccessfully,
	};
};

const actions = {
	showFilteredNodesNames,
	showFilteredNodes,
	getNodes,
};

export default connect(mapStateToProps, actions)(NodeSearchBar);
